import { useEffect, useState } from "react";
import { Plus, Save, Trash2, LayoutGrid, RefreshCw, Instagram, Twitter } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const inputCls = "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

export default function StatsTab() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [form, setForm] = useState({ label: "", value: "" });
  const [followers, setFollowers] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadFollowers = () => api.get("/social/followers").then((r) => setFollowers(r.data)).catch(() => {});

  const refreshFollowers = async () => {
    setRefreshing(true);
    try {
      await api.post("/admin/social/followers/refresh");
      toast.success("Compteurs actualisés");
      loadFollowers();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRefreshing(false); }
  };

  const load = () => api.get("/stats").then((r) => {
    const list = r.data.items || [];
    setItems(list);
    setDrafts(Object.fromEntries(list.map((s) => [s.id, { ...s }])));
  }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); loadFollowers(); }, []);

  const setDraft = (id, patch) => setDrafts({ ...drafts, [id]: { ...drafts[id], ...patch } });

  const save = async (id) => {
    try {
      await api.put(`/admin/stats/${id}`, drafts[id]);
      toast.success("Cadre mis à jour");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/stats/${id}`);
      toast.success("Cadre supprimé");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/stats", form);
      toast.success("Cadre ajouté à l'accueil");
      setForm({ label: "", value: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="grid max-w-3xl gap-6" data-testid="stats-admin">
      <div className="card-tactical p-6">
        <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
          <LayoutGrid className="h-5 w-5 text-orange-500" /> Cadres de statistiques de l'accueil
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Ces cadres s'affichent sur la page d'accueil. Exemples : « Victoires LoL — 12 », « Défaites TFT — 3 », « Supporters — 96 ».
        </p>
      </div>

      <div className="card-tactical p-6" data-testid="social-followers-admin">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
            <Instagram className="h-5 w-5 text-orange-500" /> Compteurs de réseaux automatiques
          </p>
          {followers && (followers.configured?.x || followers.configured?.instagram) && (
            <button onClick={refreshFollowers} disabled={refreshing} data-testid="social-followers-refresh"
              className="flex items-center gap-2 rounded-lg border border-orange-500/40 px-3 py-1.5 text-xs font-bold uppercase text-orange-400 transition-all hover:bg-orange-500/10 disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Actualiser
            </button>
          )}
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Quand les clés API sont renseignées, deux cadres « Abonnés Instagram » et « Abonnés X » s'ajoutent automatiquement à l'accueil (mis à jour toutes les heures). Configuration : variables X_BEARER_TOKEN, X_USERNAME, INSTAGRAM_ACCESS_TOKEN et INSTAGRAM_USER_ID — demandez-moi de les brancher quand vous les avez.
        </p>
        {followers && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold uppercase">
            <span className={`flex items-center gap-1.5 rounded border px-2 py-1 ${followers.configured?.x ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-slate-600 text-slate-400"}`} data-testid="social-status-x">
              <Twitter className="h-3.5 w-3.5" /> X : {followers.configured?.x ? "connecté" : "non configuré"}
              {followers.data?.find((f) => f.provider === "x") && ` — ${followers.data.find((f) => f.provider === "x").followers.toLocaleString("fr-FR")}`}
            </span>
            <span className={`flex items-center gap-1.5 rounded border px-2 py-1 ${followers.configured?.instagram ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-slate-600 text-slate-400"}`} data-testid="social-status-instagram">
              <Instagram className="h-3.5 w-3.5" /> Instagram : {followers.configured?.instagram ? "connecté" : "non configuré"}
              {followers.data?.find((f) => f.provider === "instagram") && ` — ${followers.data.find((f) => f.provider === "instagram").followers.toLocaleString("fr-FR")}`}
            </span>
          </div>
        )}
      </div>

      {items.map((s) => {
        const d = drafts[s.id] || s;
        return (
          <div key={s.id} data-testid={`admin-stat-row-${s.id}`} className="card-tactical grid gap-3 p-4 sm:grid-cols-[1fr_140px_auto_auto] sm:items-end">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
              Libellé
              <input value={d.label} data-testid={`admin-stat-label-${s.id}`}
                onChange={(e) => setDraft(s.id, { label: e.target.value })} className={inputCls + " mt-1"} />
            </label>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
              Valeur
              <input value={d.value} data-testid={`admin-stat-value-${s.id}`}
                onChange={(e) => setDraft(s.id, { value: e.target.value })} className={inputCls + " mt-1"} />
            </label>
            <button onClick={() => save(s.id)} data-testid={`admin-stat-save-${s.id}`}
              className="flex items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
              <Save className="h-4 w-4" /> Enregistrer
            </button>
            <button onClick={() => remove(s.id)} data-testid={`admin-stat-delete-${s.id}`}
              className="flex items-center justify-center gap-2 rounded-lg border border-red-500/40 px-4 py-2.5 text-sm font-bold uppercase text-red-400 transition-all hover:bg-red-500/10">
              <Trash2 className="h-4 w-4" /> Supprimer
            </button>
          </div>
        );
      })}

      <form onSubmit={create} className="card-tactical grid gap-3 p-4 sm:grid-cols-[1fr_140px_auto] sm:items-end" data-testid="admin-new-stat-form">
        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Nouveau libellé
          <input placeholder="Ex : Victoires LoL" value={form.label} data-testid="admin-new-stat-label"
            onChange={(e) => setForm({ ...form, label: e.target.value })} className={inputCls + " mt-1"} required />
        </label>
        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Valeur
          <input placeholder="Ex : 12" value={form.value} data-testid="admin-new-stat-value"
            onChange={(e) => setForm({ ...form, value: e.target.value })} className={inputCls + " mt-1"} required />
        </label>
        <button type="submit" data-testid="admin-new-stat-submit"
          className="flex items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
          <Plus className="h-4 w-4" /> Ajouter
        </button>
      </form>
    </div>
  );
}

